/**
 * certificatesApi — certificate writes, on Symfony (`/api/v1/user/certificate…`
 * or the org-scoped routes; `credentialsApi` picks the scope).
 *
 * The server derives medical expiries (class + age, or the client's date for a
 * special issuance) and the reminder times, and enforces the rating rules
 * (symphony-holdshort `5a63c81e`). Ratings are their own resource: added by
 * rating id, removed by RELATION id.
 *
 * A medical certificate is created without a number (symphony-holdshort
 * `48a05b91`); a blank `limitations` is refused, the page sends NONE.
 */
import CredentialsApi from './credentialsApi.js';

/**
 * Create a certificate, then attach its ratings.
 *
 * @param {Object} body       CreateCertificateRequestDto fields
 * @param {string[]} ratingIds ratings to attach (no privilege)
 */
export async function addCertificate(body, ratingIds = []) {
	const created = await CredentialsApi.createCertificate(body);
	const id = created && created.id;
	for (const ratingId of ratingIds) {
		// eslint-disable-next-line no-await-in-loop
		await CredentialsApi.addCertificateRating(id, Number(ratingId));
	}
	return created;
}

/**
 * Save a certificate and bring its ratings to the chosen set.
 *
 * Removals go first: the API refuses a type/grade change while a rating that
 * no longer fits is still attached. Additions go last, once the certificate
 * has its new type and grade.
 *
 * @param {number|string} id
 * @param {Object} body              UpdateCertificateRequestDto fields
 * @param {{add: string[], remove: string[]}} ratings rating ids to add, relation ids to remove
 */
export async function saveCertificate(id, body, { add = [], remove = [] } = {}) {
	for (const relId of remove) {
		// eslint-disable-next-line no-await-in-loop
		await CredentialsApi.removeCertificateRating(id, relId);
	}
	const saved = await CredentialsApi.updateCertificate(id, body);
	for (const ratingId of add) {
		// eslint-disable-next-line no-await-in-loop
		await CredentialsApi.addCertificateRating(id, Number(ratingId));
	}
	return saved;
}

export const deleteCertificate = certificateId => CredentialsApi.removeCertificate(certificateId);
